import { create } from 'zustand';

import { send } from '@/api/send';
import type { PrefsMap } from '@/api/types';

export type ThemeName = 'light' | 'dark' | 'midnight' | 'auto';

/** 'refined' = 墨记皮肤；'clone' = 上游 Actual 原样观感 */
export type SkinName = 'refined' | 'clone';

export function applySkin(skin: SkinName | string | undefined): void {
  document.documentElement.dataset.skin = skin === 'clone' ? 'clone' : 'refined';
}

/**
 * Themes are pure CSS custom-property swaps in src/style/themes, so applying
 * one is just an attribute on <html>. `auto` resolves against the OS setting
 * the same way loot-core's `usePreferredDarkTheme` does.
 */
export function resolveTheme(
  theme: ThemeName,
  preferredDark: string,
): 'light' | 'dark' | 'midnight' {
  if (theme !== 'auto') return theme;
  const dark =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches;
  if (!dark) return 'light';
  return preferredDark === 'midnight' ? 'midnight' : 'dark';
}

export function applyTheme(theme: ThemeName, preferredDark: string): void {
  const resolved = resolveTheme(theme, preferredDark);
  document.documentElement.dataset.theme = resolved;
  document.documentElement.style.colorScheme =
    resolved === 'light' ? 'light' : 'dark';
}

type PrefsState = {
  ready: boolean;
  global: PrefsMap;
  budget: PrefsMap;
  load: () => Promise<void>;
  setGlobalPref: (key: string, value: string) => void;
  setBudgetPref: (key: string, value: string) => void;
};

export const usePrefs = create<PrefsState>((set, get) => ({
  ready: false,
  global: {},
  budget: {},

  async load() {
    const [globalPrefs, budgetPrefs] = await Promise.all([
      send('load-global-prefs'),
      send('load-prefs'),
    ]);
    set({ global: globalPrefs, budget: budgetPrefs, ready: true });
    applySkin(globalPrefs.skin);
    applyTheme(
      (globalPrefs.theme as ThemeName) ?? 'light',
      globalPrefs.preferredDarkTheme ?? 'dark',
    );
  },

  setGlobalPref(key, value) {
    const global = { ...get().global, [key]: value };
    set({ global });
    if (key === 'skin') applySkin(value);
    if (key === 'theme' || key === 'preferredDarkTheme') {
      applyTheme(
        (global.theme as ThemeName) ?? 'light',
        global.preferredDarkTheme ?? 'dark',
      );
    }
    void send('save-global-prefs', { diff: { [key]: value } });
  },

  setBudgetPref(key, value) {
    set({ budget: { ...get().budget, [key]: value } });
    void send('save-prefs', { diff: { [key]: value } });
  },
}));

/** Mirrors upstream's `useGlobalPref(name)` -> [value, setter]. */
export function useGlobalPref(name: string): [string | undefined, (v: string) => void] {
  const value = usePrefs(state => state.global[name]);
  const setPref = usePrefs(state => state.setGlobalPref);
  return [value, (next: string) => setPref(name, next)];
}

/** Budget-scoped pref, covering the synced and per-user maps together. */
export function usePref(name: string): [string | undefined, (v: string) => void] {
  const value = usePrefs(state => state.budget[name]);
  const setPref = usePrefs(state => state.setBudgetPref);
  return [value, (next: string) => setPref(name, next)];
}

export function useBoolPref(name: string, fallback = false): [boolean, (v: boolean) => void] {
  const [raw, set] = usePref(name);
  const value = raw === undefined ? fallback : raw === 'true';
  return [value, next => set(String(next))];
}
